/**
 * The Manufacturing module's route table (CLAUDE.md §8.15).
 *
 * `src/router/index.ts` is shared by every module, so this file declares the
 * records and the router spreads `MFG_ROUTES` in. Keeping them here means the
 * paths, the permissions and the components that implement them are decided in
 * one place by whoever owns the module.
 *
 * Every `:id` segment carries the `\d+` constraint, so no literal word can ever
 * be parsed as a document id, and the list route of each family is declared
 * before its detail route so the ordering says the same thing the constraint
 * enforces.
 *
 * Which permission guards which screen follows the API rather than the topic.
 * A screen is guarded by the code its **read** endpoint declares; the narrower
 * codes that let a user act — release, issue, receive, close, activate — are
 * checked by the screens themselves, per action, because §6 makes those a
 * segregation-of-duties chain and a user who may look at a production order
 * very often may not be the one who closes it.
 *
 * `MFG_PATHS` is the same set as strings, for links inside the module. Links
 * *out* of it — the journal a posting produced, the stock ledger behind a
 * receipt, the item a BOM line names — are built here and then checked against
 * the router by `linkIfRoutable` before being rendered. Those URLs belong to GL
 * and Inventory, and a guessed link that 404s in the middle of a cost
 * investigation is worse than plain text.
 */

import type { Router, RouteRecordRaw } from 'vue-router'

import { MFG_PERMISSIONS } from '@/modules/mfg/permissions'
import { MFG_C7_ROUTES } from '@/modules/mfg/routesC7'

export const MFG_ROUTES: RouteRecordRaw[] = [
  {
    path: '/mfg/production-orders',
    name: 'mfg-production-orders',
    component: () => import('@/modules/mfg/ProductionOrdersListView.vue'),
    meta: {
      titleKey: 'mfg.routeTitles.productionOrders',
      permission: MFG_PERMISSIONS.orderRead,
    },
  },
  {
    path: '/mfg/production-orders/:id(\\d+)',
    name: 'mfg-production-order',
    component: () => import('@/modules/mfg/ProductionOrderView.vue'),
    meta: {
      titleKey: 'mfg.routeTitles.productionOrder',
      permission: MFG_PERMISSIONS.orderRead,
    },
  },

  {
    path: '/mfg/boms',
    name: 'mfg-boms',
    component: () => import('@/modules/mfg/BomsListView.vue'),
    meta: {
      titleKey: 'mfg.routeTitles.boms',
      permission: MFG_PERMISSIONS.bomRead,
    },
  },
  {
    path: '/mfg/boms/:id(\\d+)',
    name: 'mfg-bom',
    component: () => import('@/modules/mfg/BomView.vue'),
    meta: {
      titleKey: 'mfg.routeTitles.bom',
      permission: MFG_PERMISSIONS.bomRead,
    },
  },

  {
    path: '/mfg/routings',
    name: 'mfg-routings',
    component: () => import('@/modules/mfg/RoutingsListView.vue'),
    meta: {
      titleKey: 'mfg.routeTitles.routings',
      permission: MFG_PERMISSIONS.routingRead,
    },
  },
  {
    path: '/mfg/routings/:id(\\d+)',
    name: 'mfg-routing',
    component: () => import('@/modules/mfg/RoutingView.vue'),
    meta: {
      titleKey: 'mfg.routeTitles.routing',
      permission: MFG_PERMISSIONS.routingRead,
    },
  },

  {
    path: '/mfg/work-centers',
    name: 'mfg-work-centers',
    component: () => import('@/modules/mfg/WorkCentersListView.vue'),
    meta: {
      titleKey: 'mfg.routeTitles.workCenters',
      permission: MFG_PERMISSIONS.workCenterRead,
    },
  },

  {
    path: '/mfg/standard-costs',
    name: 'mfg-standard-costs',
    component: () => import('@/modules/mfg/StandardCostsView.vue'),
    meta: {
      titleKey: 'mfg.routeTitles.standardCosts',
      permission: MFG_PERMISSIONS.standardCostRead,
    },
  },

  {
    path: '/mfg/mrp',
    name: 'mfg-mrp',
    component: () => import('@/modules/mfg/MrpView.vue'),
    meta: {
      titleKey: 'mfg.routeTitles.mrp',
      permission: MFG_PERMISSIONS.mrpRead,
    },
  },

  {
    path: '/mfg/reports/capacity',
    name: 'mfg-capacity',
    component: () => import('@/modules/mfg/CapacityView.vue'),
    meta: {
      titleKey: 'mfg.routeTitles.capacity',
      permission: MFG_PERMISSIONS.reportRead,
    },
  },
  {
    path: '/mfg/reports/wip-clearing',
    name: 'mfg-wip-clearing',
    component: () => import('@/modules/mfg/WipClearingView.vue'),
    meta: {
      titleKey: 'mfg.routeTitles.wipClearing',
      permission: MFG_PERMISSIONS.reportRead,
    },
  },

  {
    path: '/mfg/settings',
    name: 'mfg-settings',
    component: () => import('@/modules/mfg/SettingsView.vue'),
    meta: {
      titleKey: 'mfg.routeTitles.settings',
      permission: MFG_PERMISSIONS.settingsRead,
    },
  },

  /* C7's additions (§5.5) live in their own file so a milestone's routes are
     reviewable as a block; the router still imports one array. */
  ...MFG_C7_ROUTES,
]

/* ------------------------------------------------------------ own screens */

export const MFG_PATHS = {
  productionOrders: '/mfg/production-orders',
  productionOrder: (id: number) => `/mfg/production-orders/${id}`,
  boms: '/mfg/boms',
  bom: (id: number) => `/mfg/boms/${id}`,
  routings: '/mfg/routings',
  routing: (id: number) => `/mfg/routings/${id}`,
  workCenters: '/mfg/work-centers',
  standardCosts: '/mfg/standard-costs',
  mrp: '/mfg/mrp',
  capacity: '/mfg/reports/capacity',
  wipClearing: '/mfg/reports/wip-clearing',
  settings: '/mfg/settings',
} as const

/** The production-order list, pre-filtered — what a KPI or a report drills into. */
export function ordersRoute(params: {
  companyId?: number | null
  status?: string
  itemId?: number | null
}): string {
  const query = new URLSearchParams()
  if (params.companyId !== null && params.companyId !== undefined) {
    query.set('companyId', String(params.companyId))
  }
  if (params.status !== undefined && params.status !== '') query.set('status', params.status)
  if (params.itemId !== null && params.itemId !== undefined) {
    query.set('itemId', String(params.itemId))
  }
  const qs = query.toString()
  return qs === '' ? MFG_PATHS.productionOrders : `${MFG_PATHS.productionOrders}?${qs}`
}

/** The BOM list narrowed to one item — the hop from an order to its structure. */
export function bomsForItemRoute(companyId: number, itemId: number): string {
  return `${MFG_PATHS.boms}?companyId=${companyId}&itemId=${itemId}`
}

/** The routing list narrowed to one item. */
export function routingsForItemRoute(companyId: number, itemId: number): string {
  return `${MFG_PATHS.routings}?companyId=${companyId}&itemId=${itemId}`
}

/** Standard costs narrowed to one item — "as entered", never rolled up. */
export function standardCostsForItemRoute(companyId: number, itemId: number): string {
  return `${MFG_PATHS.standardCosts}?companyId=${companyId}&itemId=${itemId}`
}

/* -------------------------------------------------------- links elsewhere */

/** The journal a posting produced. GL owns this URL. */
export function journalRoute(entryId: number): string {
  return `/gl/journal-entries/${entryId}`
}

/** One item in inventory's catalogue. */
export function itemRoute(itemId: number): string {
  return `/inventory/items/${itemId}`
}

/**
 * The stock ledger, narrowed to the movements one production order made.
 *
 * Every issue and every finished-goods receipt writes stock ledger entries under
 * `mfg.production_order` and this order's id, which is what turns "WIP moved"
 * into the individual movements behind it.
 */
export function stockLedgerRoute(orderId: number, itemId?: number | null): string {
  const query = new URLSearchParams({ docType: 'mfg.production_order', docId: String(orderId) })
  if (itemId !== null && itemId !== undefined) query.set('itemId', String(itemId))
  return `/inventory/stock-ledger?${query.toString()}`
}

/**
 * The path, but only if this app actually has a screen for it.
 *
 * The router's catch-all matches everything, so "resolves" is not the question —
 * "resolves to something other than the not-found page" is. A path the router
 * would send to the 404 screen comes back as null and the caller renders text.
 */
export function linkIfRoutable(router: Router, path: string | null): string | null {
  if (path === null || path === '') return null
  try {
    const resolved = router.resolve(path)
    if (resolved.matched.length === 0) return null
    if (resolved.matched.every((record) => record.name === 'not-found')) return null
    return path
  } catch {
    // An unparseable path is not a link either.
    return null
  }
}
